import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Container,
  TextField,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { getEventSongsData } from "../data/songsData";
import { lineupService } from "../services/lineupService";
import { fantaService } from "../services/fantaService";
import { eventService } from "../services/eventService";
import { songService } from "../services/songService";
import { functions } from "../firebase/config";
import { artistImages, eurovisionArtistImages } from "../assets/artistImages";
import {
  KeyboardArrowUp,
  KeyboardArrowDown,
  Save,
  Delete,
  AddCircle,
  Edit,
} from "@mui/icons-material";
import {
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Grid,
  Chip,
} from "@mui/material";

const AdminPage = () => {
  const navigate = useNavigate();
  const { eventKey } = useParams();
  const eventId = eventKey || "sanremo";
  const isEurovision = eventId === "eurovision";
  const {
    nights,
    songs,
    categories: legacyCategories,
  } = getEventSongsData(eventId);
  const fallbackNightId = parseInt(Object.keys(nights)[0], 10) || 1;
  const projectId = functions?.app?.options?.projectId || "esc-voting-10822";
  const publicVotesApiUrl = `https://us-central1-${projectId}.cloudfunctions.net/api/votes/latest?eventId=${encodeURIComponent(
    eventId,
  )}`;
  const { user } = useSelector((state) => state.auth);

  // Active Night State
  const [activeNightId, setActiveNightId] = useState(1);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [savingEventConfig, setSavingEventConfig] = useState(false);
  const [subEvents, setSubEvents] = useState([]);
  const [categoryConfig, setCategoryConfig] = useState([]);
  const [resultsRevealed, setResultsRevealed] = useState(false);
  const configDirtyRef = useRef(false);

  // Lineup State
  const [lineupNightId, setLineupNightId] = useState(1);
  const [lineupSongs, setLineupSongs] = useState([]);
  const [loadingLineup, setLoadingLineup] = useState(false);
  const [savingLineup, setSavingLineup] = useState(false);
  const [eventSongs, setEventSongs] = useState([]);
  const [loadingEventSongs, setLoadingEventSongs] = useState(true);
  const [importingSongs, setImportingSongs] = useState(false);
  const [creatingSong, setCreatingSong] = useState(false);
  const [newSong, setNewSong] = useState({
    key: "",
    label: "",
    artist: "",
    title: "",
    order: "",
    subEventIds: [],
  });
  const [segmentProps, setSegmentProps] = useState([{ key: "", value: "" }]);
  const [segmentImageFile, setSegmentImageFile] = useState(null);
  const [segmentImagePreview, setSegmentImagePreview] = useState("");
  const [uploadingLegacyImages, setUploadingLegacyImages] = useState(false);

  // Fanta State
  const [fantaResults, setFantaResults] = useState([
    null,
    null,
    null,
    null,
    null,
  ]);
  const [fantaRevealed, setFantaRevealed] = useState(false);
  const [fantaLocked, setFantaLocked] = useState(false);
  const [savingFanta, setSavingFanta] = useState(false);

  // Eventi State
  const [events, setEvents] = useState([]);
  const [creating, setCreating] = useState(false);
  const [seedingEvents, setSeedingEvents] = useState(false);
  const [newEvent, setNewEvent] = useState({
    key: "",
    title: "",
    subtitle: "",
  });
  const [eventsError, setEventsError] = useState(null);

  // Admin check
  const isAdmin = user?.email === "admin@esc.com" || user?.role === "admin";

  const defaultSubEvents = React.useMemo(() => {
    return Object.values(nights)
      .map((night) => ({
        id: Number(night.id),
        label: night.label,
        order: Number(night.id),
        isVotingOpen: true,
        scheduleKey: night.scheduleKey || null,
        isCoverNight: Boolean(night.isCoverNight),
        defaultOrder: Array.isArray(night.defaultOrder)
          ? night.defaultOrder
          : null,
      }))
      .sort((a, b) => a.order - b.order);
  }, [nights]);

  const defaultCategoryConfig = React.useMemo(() => {
    return (legacyCategories || []).map((c, index) => ({
      key: String(c.key || "").trim(),
      label: String(c.label || "").trim(),
      max: Number.isFinite(Number(c.max)) ? Number(c.max) : 10,
      order: index + 1,
    }));
  }, [legacyCategories]);

  useEffect(() => {
    if (!isAdmin && !loadingSettings) {
      // Redirect if not admin (double check in case route protection fails or for UX)
      // Although ProtectedRoute should handle this if we make an AdminRoute,
      // for now we rely on the check here or just render "Access Denied"
    }
  }, [isAdmin, loadingSettings, navigate]);

  useEffect(() => {
    if (!nights[activeNightId]) {
      setActiveNightId(fallbackNightId);
    }
    if (!nights[lineupNightId]) {
      setLineupNightId(fallbackNightId);
    }
  }, [eventId, nights, activeNightId, lineupNightId, fallbackNightId]);

  useEffect(() => {
    if (isEurovision) {
      setActiveNightId(1);
      setLineupNightId(1);
    }
  }, [isEurovision]);

  // Load settings
  useEffect(() => {
    setLoadingSettings(true);
    configDirtyRef.current = false;
    const unsubscribe = eventService.subscribeToEvent(
      eventId,
      (ev) => {
        if (!configDirtyRef.current) {
          if (isEurovision) {
            setActiveNightId(1);
            const remote = Array.isArray(ev?.subEvents) ? ev.subEvents : [];
            const existing = remote.find((s) => Number(s.id) === 1);
            const votingOpen =
              existing && typeof existing.isVotingOpen === "boolean"
                ? existing.isVotingOpen
                : true;
            setSubEvents([
              {
                ...defaultSubEvents[0],
                isVotingOpen: votingOpen,
              },
            ]);
            setResultsRevealed(Boolean(ev?.resultsRevealed));
            const nextCategories =
              Array.isArray(ev?.categories) && ev.categories.length > 0
                ? ev.categories
                : defaultCategoryConfig;
            setCategoryConfig(nextCategories);
            setLoadingSettings(false);
            return;
          }
          const remoteNightId = Number(ev?.currentSubEventId);
          const nextActiveNightId =
            Number.isFinite(remoteNightId) && nights[remoteNightId]
              ? remoteNightId
              : fallbackNightId;
          setActiveNightId(nextActiveNightId);

          const nextSubEvents =
            Array.isArray(ev?.subEvents) && ev.subEvents.length > 0
              ? ev.subEvents
              : defaultSubEvents;
          setSubEvents(nextSubEvents);

          const nextCategories =
            Array.isArray(ev?.categories) && ev.categories.length > 0
              ? ev.categories
              : defaultCategoryConfig;
          setCategoryConfig(nextCategories);
        }
        setLoadingSettings(false);
      },
      (error) => {
        console.warn("Failed to load event config, using default:", error);
        if (!configDirtyRef.current) {
          if (isEurovision) {
            setActiveNightId(1);
            setSubEvents([
              {
                ...defaultSubEvents[0],
                isVotingOpen: true,
              },
            ]);
            setCategoryConfig(defaultCategoryConfig);
            setResultsRevealed(false);
          } else {
            setActiveNightId(fallbackNightId);
            setSubEvents(defaultSubEvents);
            setCategoryConfig(defaultCategoryConfig);
          }
        }
        setLoadingSettings(false);
      },
    );
    return () => unsubscribe();
  }, [
    eventId,
    fallbackNightId,
    isEurovision,
    nights,
    defaultSubEvents,
    defaultCategoryConfig,
  ]);

  useEffect(() => {
    setLoadingEventSongs(true);
    const unsubscribe = songService.subscribeToSongs(
      eventId,
      (items) => {
        setEventSongs(items || []);
        setLoadingEventSongs(false);
      },
      () => {
        setEventSongs([]);
        setLoadingEventSongs(false);
      },
    );
    return () => unsubscribe();
  }, [eventId]);

  // Fanta Logic
  useEffect(() => {
    const unsubscribe = fantaService.subscribeToResults((data) => {
      if (data) {
        const loadedResults = data.results ? [...data.results] : [];
        while (loadedResults.length < 5) loadedResults.push(null);
        setFantaResults(loadedResults);
        setFantaRevealed(data.isRevealed || false);
        setFantaLocked(data.isLocked || false);
      }
    }, eventId);
    return () => unsubscribe();
  }, [eventId]);

  // Eventi Logic
  useEffect(() => {
    const unsub = eventService.subscribeToAllEvents(
      (items) => {
        setEvents(items || []);
      },
      (e) => {
        setEventsError(e?.message || "Errore caricamento eventi");
      },
    );
    return () => {
      if (typeof unsub === "function") unsub();
    };
  }, []);

  const handleCreateEvent = async () => {
    try {
      setCreating(true);
      await eventService.createEvent({
        key: newEvent.key.trim(),
        title: newEvent.title.trim(),
        subtitle: newEvent.subtitle.trim(),
      });
      setNewEvent({ key: "", title: "", subtitle: "" });
    } catch (e) {
      alert(e.message || "Errore creazione evento");
    } finally {
      setCreating(false);
    }
  };

  const handleSeedEvents = async () => {
    try {
      setSeedingEvents(true);
      const sampleEvents = [
        {
          key: "sanremo",
          title: "Sanremo",
          subtitle: "Evento demo (legacy data in app)",
        },
        {
          key: "eurovision",
          title: "Eurovision",
          subtitle: "Evento demo (legacy data in app)",
        },
      ];

      for (const ev of sampleEvents) {
        const existing = await eventService.getEvent(ev.key);
        if (!existing) {
          await eventService.createEvent(ev);
        } else {
          await eventService.updateEvent(ev.key, {
            title: ev.title,
            subtitle: ev.subtitle,
          });
        }
        await eventService.publishEvent(ev.key);
      }
      alert("Eventi di esempio creati e pubblicati.");
    } catch (e) {
      alert(e.message || "Errore seed eventi");
    } finally {
      setSeedingEvents(false);
    }
  };

  const togglePublish = async (ev) => {
    try {
      if (ev.status === "published") {
        await eventService.unpublishEvent(ev.key);
      } else {
        await eventService.publishEvent(ev.key);
      }
    } catch (e) {
      alert(e.message || "Errore aggiornamento stato evento");
    }
  };

  const handleFantaArtistChange = (index, artistKey) => {
    const newResults = [...fantaResults];
    newResults[index] = artistKey;
    setFantaResults(newResults);
  };

  const saveFantaResults = async () => {
    try {
      setSavingFanta(true);
      await fantaService.setFantaResults(
        fantaResults,
        fantaRevealed,
        fantaLocked,
        eventId,
      );
      alert("Risultati Fanta-Sanremo salvati!");
    } catch (error) {
      console.error("Errore salvataggio Fanta:", error);
      alert("Errore nel salvataggio.");
    } finally {
      setSavingFanta(false);
    }
  };

  const toggleReveal = async () => {
    try {
      setSavingFanta(true);
      const newRevealed = !fantaRevealed;
      setFantaRevealed(newRevealed);
      await fantaService.setFantaResults(
        fantaResults,
        newRevealed,
        fantaLocked,
        eventId,
      );
    } catch (error) {
      console.error("Errore toggle reveal:", error);
      alert("Errore nell'aggiornamento dello stato.");
      setFantaRevealed(!fantaRevealed);
    } finally {
      setSavingFanta(false);
    }
  };

  const toggleLock = async () => {
    try {
      setSavingFanta(true);
      const newLocked = !fantaLocked;
      setFantaLocked(newLocked);
      await fantaService.setFantaResults(
        fantaResults,
        fantaRevealed,
        newLocked,
        eventId,
      );
    } catch (error) {
      console.error("Errore toggle lock:", error);
      alert("Errore nell'aggiornamento dello stato di blocco.");
      setFantaLocked(!fantaLocked);
    } finally {
      setSavingFanta(false);
    }
  };

  const handleActiveNightChange = (event) => {
    const newNightId = parseInt(event.target.value, 10);
    configDirtyRef.current = true;
    setActiveNightId(newNightId);
  };

  const sanitizeCategories = (items) => {
    const cleaned = (items || [])
      .map((c) => ({
        key: String(c.key || "").trim(),
        label: String(c.label || "").trim(),
        max: Number(c.max),
        order: Number(c.order),
      }))
      .filter((c) => c.key && c.label)
      .map((c, index) => ({
        ...c,
        max: Number.isFinite(c.max) && c.max > 0 ? c.max : 10,
        order: Number.isFinite(c.order) ? c.order : index + 1,
      }))
      .sort((a, b) => a.order - b.order);

    const seen = new Set();
    const deduped = [];
    for (const c of cleaned) {
      if (seen.has(c.key)) continue;
      seen.add(c.key);
      deduped.push(c);
    }
    return deduped;
  };

  const sanitizeSubEvents = (items) => {
    const cleaned = (items || [])
      .map((s) => ({
        id: Number(s.id),
        label: String(s.label || "").trim(),
        order: Number(s.order),
        isVotingOpen: Boolean(s.isVotingOpen),
        scheduleKey: s.scheduleKey ? String(s.scheduleKey).trim() : null,
        isCoverNight: Boolean(s.isCoverNight),
        defaultOrder: Array.isArray(s.defaultOrder) ? s.defaultOrder : null,
      }))
      .filter((s) => Number.isFinite(s.id) && s.label)
      .map((s, index) => ({
        ...s,
        order: Number.isFinite(s.order) ? s.order : index + 1,
      }))
      .sort((a, b) => a.order - b.order);

    const seen = new Set();
    const deduped = [];
    for (const s of cleaned) {
      if (seen.has(s.id)) continue;
      seen.add(s.id);
      deduped.push(s);
    }
    return deduped;
  };

  const saveEventConfig = async () => {
    setSavingEventConfig(true);
    try {
      const nextCategories = sanitizeCategories(categoryConfig);
      const nextSubEvents = isEurovision
        ? [
            {
              ...defaultSubEvents[0],
              isVotingOpen: Boolean(subEvents?.[0]?.isVotingOpen),
            },
          ]
        : sanitizeSubEvents(subEvents);

      const targetSubEvent = isEurovision
        ? 1
        : Number.isFinite(activeNightId) &&
            nextSubEvents.some((s) => s.id === activeNightId)
          ? activeNightId
          : nextSubEvents[0]?.id || fallbackNightId;

      const payload = {
        categories: nextCategories,
        subEvents: nextSubEvents,
        currentSubEventId: targetSubEvent,
      };
      if (isEurovision) {
        payload.resultsRevealed = Boolean(resultsRevealed);
      }
      await eventService.updateEvent(eventId, payload);

      configDirtyRef.current = false;
      alert("Impostazioni evento salvate con successo!");
    } catch (error) {
      console.error("Errore salvataggio impostazioni evento:", error);
      alert("Errore durante il salvataggio delle impostazioni.");
    } finally {
      setSavingEventConfig(false);
    }
  };

  const resetEventConfigFromLegacy = () => {
    configDirtyRef.current = true;
    setActiveNightId(isEurovision ? 1 : fallbackNightId);
    setSubEvents(defaultSubEvents);
    setCategoryConfig(defaultCategoryConfig);
    setResultsRevealed(false);
  };

  useEffect(() => {
    const fetchLineup = async () => {
      setLoadingLineup(true);
      try {
        const effectiveLineupNightId = nights[lineupNightId]
          ? lineupNightId
          : fallbackNightId;
        const sourceSongs = eventSongs.length > 0 ? eventSongs : songs;
        const eligibleSongs = sourceSongs.filter((s) => s && s.key);

        // Get saved lineup
        let savedLineupKeys = await lineupService.getLineup(
          effectiveLineupNightId,
          eventId,
        );
        if (
          isEurovision &&
          (!savedLineupKeys || savedLineupKeys.length === 0)
        ) {
          const legacyFinalKeys = await lineupService.getLineup(3, eventId);
          if (legacyFinalKeys && legacyFinalKeys.length > 0) {
            savedLineupKeys = legacyFinalKeys;
          }
        }

        let finalSongs = eligibleSongs;
        if (savedLineupKeys && savedLineupKeys.length > 0) {
          finalSongs = savedLineupKeys
            .map((key) => eligibleSongs.find((s) => s.key === key))
            .filter(Boolean);
        }

        setLineupSongs(finalSongs);
      } catch (error) {
        console.error("Error fetching lineup:", error);
      } finally {
        setLoadingLineup(false);
      }
    };

    fetchLineup();
  }, [
    lineupNightId,
    eventId,
    nights,
    songs,
    fallbackNightId,
    eventSongs,
    isEurovision,
  ]);

  const handleLineupNightChange = (event) => {
    setLineupNightId(parseInt(event.target.value, 10));
  };

  const moveSong = (index, direction) => {
    const newSongs = [...lineupSongs];
    const targetIndex = index + direction;

    if (targetIndex < 0 || targetIndex >= newSongs.length) return;

    [newSongs[index], newSongs[targetIndex]] = [
      newSongs[targetIndex],
      newSongs[index],
    ];
    setLineupSongs(newSongs);
  };

  const addSong = (song) => {
    setLineupSongs([...lineupSongs, song]);
  };

  const removeSong = (index) => {
    const newSongs = [...lineupSongs];
    newSongs.splice(index, 1);
    setLineupSongs(newSongs);
  };

  // Calcola le canzoni disponibili (quelle non presenti nella scaletta corrente)
  const availableSongs = (eventSongs.length > 0 ? eventSongs : songs).filter(
    (s) => !lineupSongs.some((ls) => ls.key === s.key),
  );

  const handleSegmentImageChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setSegmentImageFile(file);
    if (segmentImagePreview && segmentImagePreview.startsWith("blob:")) {
      URL.revokeObjectURL(segmentImagePreview);
    }
    setSegmentImagePreview(URL.createObjectURL(file));
  };

  const uploadLegacyImagesToStorage = async () => {
    setUploadingLegacyImages(true);
    try {
      const map =
        eventId === "eurovision" ? eurovisionArtistImages : artistImages;
      const keys = Object.keys(map || {});
      let uploaded = 0;

      for (const key of keys) {
        const url = map[key];
        if (!url) continue;

        const existing = await songService.getSong(eventId, key);
        if (!existing) continue;

        const res = await fetch(url);
        if (!res.ok) continue;
        const blob = await res.blob();
        const { imageUrl, imagePath } = await songService.uploadSegmentImage(
          eventId,
          key,
          blob,
        );

        const computedLabel =
          existing.label ||
          `${existing.artist || ""}${existing.artist && existing.title ? " - " : ""}${existing.title || ""}`.trim() ||
          existing.key;

        await songService.upsertSong(eventId, {
          ...existing,
          key,
          label: computedLabel,
          imageUrl,
          imagePath,
        });
        uploaded += 1;
      }

      alert(`Immagini caricate su Storage: ${uploaded}`);
    } catch (e) {
      alert(e?.message || "Errore upload immagini legacy");
    } finally {
      setUploadingLegacyImages(false);
    }
  };

  const importSongsFromLegacy = async () => {
    setImportingSongs(true);
    try {
      const computedSongs = songs.map((song) => ({
        ...song,
        label:
          `${song.artist || ""}${song.artist && song.title ? " - " : ""}${song.title || ""}`.trim() ||
          song.key,
        subEventIds: [],
      }));

      for (const s of computedSongs) {
        await songService.upsertSong(eventId, s);
      }

      const mergedSubEvents = defaultSubEvents.map((base) => {
        const existing = subEvents.find(
          (s) => Number(s.id) === Number(base.id),
        );
        return existing
          ? {
              ...base,
              ...existing,
              scheduleKey: existing.scheduleKey ?? base.scheduleKey ?? null,
              isCoverNight: existing.isCoverNight ?? base.isCoverNight ?? false,
              defaultOrder: existing.defaultOrder ?? base.defaultOrder ?? null,
            }
          : base;
      });

      await eventService.updateEvent(eventId, { subEvents: mergedSubEvents });
      alert("Segmenti importati su Firestore.");
    } catch (e) {
      alert(e?.message || "Errore import segmenti");
    } finally {
      setImportingSongs(false);
    }
  };

  const createSong = async () => {
    setCreatingSong(true);
    try {
      const key = newSong.key.trim();
      const label = newSong.label.trim();
      const artist = newSong.artist.trim();
      const title = newSong.title.trim();
      const order = Number(newSong.order);
      const subEventIds = (newSong.subEventIds || [])
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id));

      if (!key || !label || !Number.isFinite(order)) {
        throw new Error("Compila key, label e ordine");
      }

      const extra = {};
      (segmentProps || [])
        .filter((p) => p && String(p.key || "").trim())
        .forEach((p) => {
          const k = String(p.key || "").trim();
          const raw = String(p.value ?? "").trim();
          if (!k) return;
          if (raw === "") return;
          if (
            raw === "true" ||
            raw === "false" ||
            raw === "null" ||
            /^-?\d+(\.\d+)?$/.test(raw) ||
            raw.startsWith("{") ||
            raw.startsWith("[")
          ) {
            try {
              extra[k] = JSON.parse(raw);
              return;
            } catch {
              extra[k] = raw;
              return;
            }
          }
          extra[k] = raw;
        });

      let imageFields = {};
      if (segmentImageFile) {
        const { imageUrl, imagePath } = await songService.uploadSegmentImage(
          eventId,
          key,
          segmentImageFile,
        );
        imageFields = { imageUrl, imagePath };
      }

      await songService.upsertSong(eventId, {
        key,
        label,
        artist,
        title,
        order,
        subEventIds,
        ...extra,
        ...imageFields,
      });

      setNewSong({
        key: "",
        label: "",
        artist: "",
        title: "",
        order: "",
        subEventIds: [],
      });
      setSegmentProps([{ key: "", value: "" }]);
      setSegmentImageFile(null);
      if (segmentImagePreview && segmentImagePreview.startsWith("blob:")) {
        URL.revokeObjectURL(segmentImagePreview);
      }
      setSegmentImagePreview("");
      alert("Segmento salvato.");
    } catch (e) {
      alert(e?.message || "Errore creazione segmento");
    } finally {
      setCreatingSong(false);
    }
  };

  const editSong = (song) => {
    if (!song) return;
    const baseKeys = new Set([
      "key",
      "label",
      "artist",
      "title",
      "order",
      "subEventIds",
      "createdAt",
      "updatedAt",
    ]);
    const props = Object.entries(song || {})
      .filter(([k]) => !baseKeys.has(k))
      .map(([k, v]) => ({
        key: k,
        value:
          v === null || v === undefined
            ? ""
            : typeof v === "object"
              ? JSON.stringify(v)
              : String(v),
      }));

    const computedLabel =
      `${song.artist || ""}${song.artist && song.title ? " - " : ""}${song.title || ""}`.trim() ||
      song.key ||
      "";

    setNewSong({
      key: song.key || "",
      label: song.label || computedLabel,
      artist: song.artist || "",
      title: song.title || "",
      order: Number.isFinite(Number(song.order)) ? String(song.order) : "",
      subEventIds: Array.isArray(song.subEventIds) ? song.subEventIds : [],
    });
    setSegmentProps(props.length > 0 ? props : [{ key: "", value: "" }]);
    setSegmentImageFile(null);
    if (segmentImagePreview && segmentImagePreview.startsWith("blob:")) {
      URL.revokeObjectURL(segmentImagePreview);
    }
    setSegmentImagePreview(song.imageUrl || song.image || "");
  };

  const saveLineup = async () => {
    setSavingLineup(true);
    try {
      const songKeys = lineupSongs.map((s) => s.key);
      const effectiveLineupNightId = nights[lineupNightId]
        ? lineupNightId
        : fallbackNightId;
      await lineupService.updateLineup(
        effectiveLineupNightId,
        songKeys,
        eventId,
      );
      alert("Scaletta salvata con successo!");
    } catch (error) {
      console.error("Error saving lineup:", error);
      alert("Errore durante il salvataggio della scaletta.");
    } finally {
      setSavingLineup(false);
    }
  };

  if (!isAdmin) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography variant="h4" color="error">
          Accesso Negato
        </Typography>
        <Typography variant="body1">
          Non hai i permessi per visualizzare questa pagina.
        </Typography>
      </Container>
    );
  }

  if (loadingSettings) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          Pannello Amministrazione
        </Typography>

        <Box mt={4} mb={4}>
          <Typography variant="h6" gutterBottom>
            Impostazioni Evento
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Configura categorie di voto e sotto-eventi. Il client legge questi
            dati da events/{eventId}.
          </Typography>

          <Box
            p={3}
            border="1px solid"
            borderColor="divider"
            borderRadius={2}
            bgcolor="background.default"
          >
            <Box display="flex" justifyContent="flex-end" gap={2} mb={2}>
              <Button variant="outlined" onClick={resetEventConfigFromLegacy}>
                Ripristina da legacy
              </Button>
              <Button
                variant="contained"
                onClick={saveEventConfig}
                disabled={savingEventConfig}
              >
                {savingEventConfig ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Salva impostazioni"
                )}
              </Button>
            </Box>

            {isEurovision ? (
              <>
                <Typography variant="body2" color="text.secondary">
                  Eurovision usa un solo sotto-event: Finale.
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(subEvents?.[0]?.isVotingOpen)}
                      onChange={(e) => {
                        configDirtyRef.current = true;
                        const checked = e.target.checked;
                        setSubEvents((prev) => [
                          {
                            ...(prev?.[0] || defaultSubEvents[0]),
                            isVotingOpen: checked,
                          },
                        ]);
                      }}
                    />
                  }
                  label="Voto finale aperto"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(resultsRevealed)}
                      onChange={(e) => {
                        configDirtyRef.current = true;
                        setResultsRevealed(e.target.checked);
                      }}
                    />
                  }
                  label="Risultati visibili"
                />
                <Divider sx={{ my: 3 }} />
              </>
            ) : (
              <>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Sotto-event attivo</InputLabel>
                      <Select
                        value={activeNightId}
                        label="Sotto-event attivo"
                        onChange={handleActiveNightChange}
                      >
                        {subEvents
                          .slice()
                          .sort((a, b) => Number(a.order) - Number(b.order))
                          .map((s) => (
                            <MenuItem key={s.id} value={s.id}>
                              {s.label} {s.id === activeNightId && "(Attuale)"}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                  Sotto-eventi
                </Typography>
                <Grid container spacing={2}>
                  {subEvents
                    .slice()
                    .sort((a, b) => Number(a.order) - Number(b.order))
                    .map((s, idx) => (
                      <Grid item xs={12} key={s.id}>
                        <Box
                          display="flex"
                          gap={2}
                          alignItems="center"
                          flexWrap="wrap"
                        >
                          <TextField
                            label="ID"
                            size="small"
                            value={s.id}
                            sx={{ width: 110 }}
                            disabled
                          />
                          <TextField
                            label="Label"
                            size="small"
                            value={s.label}
                            onChange={(e) => {
                              configDirtyRef.current = true;
                              const value = e.target.value;
                              setSubEvents((prev) =>
                                prev.map((x) =>
                                  x.id === s.id ? { ...x, label: value } : x,
                                ),
                              );
                            }}
                            sx={{ flex: 1, minWidth: 220 }}
                          />
                          <TextField
                            label="Ordine"
                            size="small"
                            type="number"
                            value={s.order}
                            onChange={(e) => {
                              configDirtyRef.current = true;
                              const value = Number(e.target.value);
                              setSubEvents((prev) =>
                                prev.map((x) =>
                                  x.id === s.id ? { ...x, order: value } : x,
                                ),
                              );
                            }}
                            sx={{ width: 120 }}
                          />
                          <FormControlLabel
                            control={
                              <Switch
                                checked={Boolean(s.isVotingOpen)}
                                onChange={(e) => {
                                  configDirtyRef.current = true;
                                  const checked = e.target.checked;
                                  setSubEvents((prev) =>
                                    prev.map((x) =>
                                      x.id === s.id
                                        ? { ...x, isVotingOpen: checked }
                                        : x,
                                    ),
                                  );
                                }}
                              />
                            }
                            label="Voto aperto"
                          />
                          <IconButton
                            color="error"
                            onClick={() => {
                              configDirtyRef.current = true;
                              setSubEvents((prev) =>
                                prev.filter((x) => x.id !== s.id),
                              );
                            }}
                            disabled={subEvents.length <= 1}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                        {idx < subEvents.length - 1 && (
                          <Divider sx={{ mt: 2 }} />
                        )}
                      </Grid>
                    ))}
                </Grid>
                <Box mt={2} display="flex" justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    startIcon={<AddCircle />}
                    onClick={() => {
                      configDirtyRef.current = true;
                      const ids = subEvents
                        .map((s) => Number(s.id))
                        .filter(Number.isFinite);
                      const nextId =
                        (ids.length > 0 ? Math.max(...ids) : 0) + 1;
                      setSubEvents((prev) => [
                        ...prev,
                        {
                          id: nextId,
                          label: `SubEvent ${nextId}`,
                          order: nextId,
                          isVotingOpen: true,
                        },
                      ]);
                    }}
                  >
                    Aggiungi subEvent
                  </Button>
                </Box>

                <Divider sx={{ my: 3 }} />
              </>
            )}

            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              Categorie voto
            </Typography>
            <Grid container spacing={2}>
              {categoryConfig
                .slice()
                .sort((a, b) => Number(a.order) - Number(b.order))
                .map((c, idx) => (
                  <Grid item xs={12} key={`${c.key || idx}`}>
                    <Box
                      display="flex"
                      gap={2}
                      alignItems="center"
                      flexWrap="wrap"
                    >
                      <TextField
                        label="Key"
                        size="small"
                        value={c.key}
                        onChange={(e) => {
                          configDirtyRef.current = true;
                          const value = e.target.value;
                          setCategoryConfig((prev) =>
                            prev.map((x, i) =>
                              i === idx ? { ...x, key: value } : x,
                            ),
                          );
                        }}
                        sx={{ width: 200 }}
                      />
                      <TextField
                        label="Label"
                        size="small"
                        value={c.label}
                        onChange={(e) => {
                          configDirtyRef.current = true;
                          const value = e.target.value;
                          setCategoryConfig((prev) =>
                            prev.map((x, i) =>
                              i === idx ? { ...x, label: value } : x,
                            ),
                          );
                        }}
                        sx={{ flex: 1, minWidth: 220 }}
                      />
                      <TextField
                        label="Max"
                        size="small"
                        type="number"
                        value={c.max}
                        onChange={(e) => {
                          configDirtyRef.current = true;
                          const value = Number(e.target.value);
                          setCategoryConfig((prev) =>
                            prev.map((x, i) =>
                              i === idx ? { ...x, max: value } : x,
                            ),
                          );
                        }}
                        sx={{ width: 120 }}
                      />
                      <TextField
                        label="Ordine"
                        size="small"
                        type="number"
                        value={c.order}
                        onChange={(e) => {
                          configDirtyRef.current = true;
                          const value = Number(e.target.value);
                          setCategoryConfig((prev) =>
                            prev.map((x, i) =>
                              i === idx ? { ...x, order: value } : x,
                            ),
                          );
                        }}
                        sx={{ width: 120 }}
                      />
                      <IconButton
                        color="error"
                        onClick={() => {
                          configDirtyRef.current = true;
                          setCategoryConfig((prev) =>
                            prev.filter((_, i) => i !== idx),
                          );
                        }}
                        disabled={categoryConfig.length <= 1}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                    {idx < categoryConfig.length - 1 && (
                      <Divider sx={{ mt: 2 }} />
                    )}
                  </Grid>
                ))}
            </Grid>
            <Box mt={2} display="flex" justifyContent="flex-end">
              <Button
                variant="outlined"
                startIcon={<AddCircle />}
                onClick={() => {
                  configDirtyRef.current = true;
                  setCategoryConfig((prev) => [
                    ...prev,
                    { key: "", label: "", max: 10, order: prev.length + 1 },
                  ]);
                }}
              >
                Aggiungi categoria
              </Button>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box mt={4} mb={4}>
          <Typography variant="h6" gutterBottom>
            Gestione Segmenti (DB)
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            I segmenti vengono salvati in events/{eventId}/segments. Voting e
            Artisti leggono da qui.
          </Typography>

          <Box
            p={3}
            border="1px solid"
            borderColor="divider"
            borderRadius={2}
            bgcolor="background.default"
          >
            <Box
              display="flex"
              justifyContent="space-between"
              gap={2}
              flexWrap="wrap"
            >
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Totale: {loadingEventSongs ? "..." : eventSongs.length}
                </Typography>
              </Box>
              <Box display="flex" gap={1} flexWrap="wrap">
                <Button
                  variant="outlined"
                  onClick={uploadLegacyImagesToStorage}
                  disabled={uploadingLegacyImages}
                >
                  {uploadingLegacyImages ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Carica immagini legacy"
                  )}
                </Button>
                <Button
                  variant="contained"
                  onClick={importSongsFromLegacy}
                  disabled={importingSongs}
                >
                  {importingSongs ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Importa da legacy"
                  )}
                </Button>
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              Crea / aggiorna segmento
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Key"
                  size="small"
                  fullWidth
                  value={newSong.key}
                  onChange={(e) =>
                    setNewSong((prev) => ({ ...prev, key: e.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Label"
                  size="small"
                  fullWidth
                  value={newSong.label}
                  onChange={(e) =>
                    setNewSong((prev) => ({ ...prev, label: e.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Artista (opzionale)"
                  size="small"
                  fullWidth
                  value={newSong.artist}
                  onChange={(e) =>
                    setNewSong((prev) => ({ ...prev, artist: e.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Titolo (opzionale)"
                  size="small"
                  fullWidth
                  value={newSong.title}
                  onChange={(e) =>
                    setNewSong((prev) => ({ ...prev, title: e.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  label="Ordine"
                  size="small"
                  type="number"
                  fullWidth
                  value={newSong.order}
                  onChange={(e) =>
                    setNewSong((prev) => ({ ...prev, order: e.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <FormControl fullWidth size="small">
                  <InputLabel>SubEvents</InputLabel>
                  <Select
                    multiple
                    value={newSong.subEventIds}
                    label="SubEvents"
                    onChange={(e) =>
                      setNewSong((prev) => ({
                        ...prev,
                        subEventIds: e.target.value,
                      }))
                    }
                    renderValue={(selected) =>
                      (selected || [])
                        .map((id) => {
                          const se = (subEvents || []).find(
                            (s) => Number(s.id) === Number(id),
                          );
                          return se ? se.label : String(id);
                        })
                        .join(", ")
                    }
                  >
                    {(subEvents || [])
                      .slice()
                      .sort((a, b) => Number(a.order) - Number(b.order))
                      .map((s) => (
                        <MenuItem key={s.id} value={s.id}>
                          {s.label}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                  <Button variant="outlined" component="label">
                    Seleziona immagine
                    <input
                      hidden
                      type="file"
                      accept="image/*"
                      onChange={handleSegmentImageChange}
                    />
                  </Button>
                  {segmentImagePreview ? (
                    <Box
                      component="img"
                      src={segmentImagePreview}
                      alt="Anteprima immagine segmento"
                      sx={{
                        width: 72,
                        height: 72,
                        borderRadius: 1,
                        objectFit: "cover",
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Nessuna immagine selezionata
                    </Typography>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  Proprietà extra (chiave → valore, JSON opzionale)
                </Typography>
                <Box display="flex" flexDirection="column" gap={1}>
                  {(segmentProps || []).map((p, idx) => (
                    <Box
                      key={`${idx}-${p.key}`}
                      display="flex"
                      gap={1}
                      alignItems="center"
                      flexWrap="wrap"
                    >
                      <TextField
                        label="Chiave"
                        size="small"
                        value={p.key}
                        onChange={(e) => {
                          const value = e.target.value;
                          setSegmentProps((prev) =>
                            prev.map((x, i) =>
                              i === idx ? { ...x, key: value } : x,
                            ),
                          );
                        }}
                        sx={{ width: 220 }}
                      />
                      <TextField
                        label="Valore"
                        size="small"
                        value={p.value}
                        onChange={(e) => {
                          const value = e.target.value;
                          setSegmentProps((prev) =>
                            prev.map((x, i) =>
                              i === idx ? { ...x, value } : x,
                            ),
                          );
                        }}
                        sx={{ minWidth: 260, flex: 1 }}
                      />
                      <IconButton
                        color="error"
                        onClick={() =>
                          setSegmentProps((prev) =>
                            prev.filter((_, i) => i !== idx),
                          )
                        }
                        disabled={(segmentProps || []).length <= 1}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
                <Box mt={1}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddCircle />}
                    onClick={() =>
                      setSegmentProps((prev) => [
                        ...prev,
                        { key: "", value: "" },
                      ])
                    }
                  >
                    Aggiungi proprietà
                  </Button>
                </Box>
              </Grid>
              <Grid item xs={12} md={4} display="flex" alignItems="center">
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={createSong}
                  disabled={creatingSong}
                  fullWidth
                >
                  {creatingSong ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Salva segmento"
                  )}
                </Button>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              Anteprima segmenti
            </Typography>
            {loadingEventSongs ? (
              <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress />
              </Box>
            ) : eventSongs.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nessun segmento nel DB per questo evento.
              </Typography>
            ) : (
              <List dense sx={{ maxHeight: 300, overflow: "auto" }}>
                {eventSongs.slice(0, 50).map((s) => (
                  <ListItem key={s.key}>
                    <ListItemText
                      primary={`${s.order ?? ""} — ${s.label || s.artist || s.key}`}
                      secondary={
                        s.artist && s.title
                          ? `${s.artist} — ${s.title}`
                          : s.title || s.key
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton onClick={() => editSong(s)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box mt={4} mb={4}>
          <Typography variant="h6" gutterBottom>
            Gestione Scaletta
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Riordina gli artisti per la serata selezionata. L'ordine salvato
            verrà utilizzato nell'app.
          </Typography>

          <Box
            p={3}
            border="1px solid"
            borderColor="divider"
            borderRadius={2}
            bgcolor="background.default"
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={3}
              flexWrap="wrap"
              gap={2}
            >
              {isEurovision ? (
                <Typography variant="body2" color="text.secondary">
                  Serata: Finale
                </Typography>
              ) : (
                <FormControl fullWidth size="small" sx={{ maxWidth: 300 }}>
                  <InputLabel>Seleziona Serata per Scaletta</InputLabel>
                  <Select
                    value={lineupNightId}
                    label="Seleziona Serata per Scaletta"
                    onChange={handleLineupNightChange}
                  >
                    {Object.values(nights).map((night) => (
                      <MenuItem key={night.id} value={night.id}>
                        {night.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <Button
                variant="contained"
                color="secondary"
                onClick={saveLineup}
                disabled={savingLineup || loadingLineup}
                startIcon={<Save />}
              >
                {savingLineup ? "Salvataggio..." : "Salva Scaletta"}
              </Button>
            </Box>

            {loadingLineup ? (
              <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={3}>
                {/* Scaletta Corrente */}
                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={0}
                    variant="outlined"
                    sx={{ p: 2, height: "100%", bgcolor: "white" }}
                  >
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      fontWeight="bold"
                      color="primary"
                    >
                      In Scaletta ({lineupSongs.length})
                    </Typography>
                    <Divider sx={{ mb: 1 }} />
                    <List dense sx={{ maxHeight: 600, overflow: "auto" }}>
                      {lineupSongs.map((song, index) => (
                        <React.Fragment key={song.key}>
                          <ListItem>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <Chip
                                label={index + 1}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ height: 24, minWidth: 24 }}
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={song.artist}
                              secondary={song.title}
                              primaryTypographyProps={{ fontWeight: 500 }}
                            />
                            <ListItemSecondaryAction sx={{ display: "flex" }}>
                              <IconButton
                                onClick={() => moveSong(index, -1)}
                                disabled={index === 0}
                                size="small"
                              >
                                <KeyboardArrowUp fontSize="small" />
                              </IconButton>
                              <IconButton
                                onClick={() => moveSong(index, 1)}
                                disabled={index === lineupSongs.length - 1}
                                size="small"
                              >
                                <KeyboardArrowDown fontSize="small" />
                              </IconButton>
                              <IconButton
                                onClick={() => removeSong(index)}
                                color="error"
                                size="small"
                                sx={{ ml: 1 }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                          {index < lineupSongs.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                      {lineupSongs.length === 0 && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          align="center"
                          py={4}
                        >
                          Nessuna canzone in scaletta.
                          <br />
                          Aggiungine dalla lista disponibili.
                        </Typography>
                      )}
                    </List>
                  </Paper>
                </Grid>

                {/* Canzoni Disponibili */}
                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={0}
                    variant="outlined"
                    sx={{ p: 2, height: "100%", bgcolor: "grey.50" }}
                  >
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      fontWeight="bold"
                      color="text.secondary"
                    >
                      Disponibili da Aggiungere ({availableSongs.length})
                    </Typography>
                    <Divider sx={{ mb: 1 }} />
                    <List dense sx={{ maxHeight: 600, overflow: "auto" }}>
                      {availableSongs.map((song) => (
                        <React.Fragment key={song.key}>
                          <ListItem>
                            <ListItemText
                              primary={song.artist}
                              secondary={song.title}
                            />
                            <ListItemSecondaryAction>
                              <IconButton
                                onClick={() => addSong(song)}
                                color="primary"
                                size="small"
                              >
                                <AddCircle />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                          <Divider component="li" />
                        </React.Fragment>
                      ))}
                      {availableSongs.length === 0 && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          align="center"
                          py={4}
                        >
                          Tutte le canzoni sono state aggiunte alla scaletta.
                        </Typography>
                      )}
                    </List>
                  </Paper>
                </Grid>
              </Grid>
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box mt={4} mb={4}>
          <Typography variant="h6" gutterBottom>
            API Pubblica
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Endpoint pubblico per leggere gli ultimi voti in tempo reale.
          </Typography>

          <Box
            p={3}
            border="1px solid"
            borderColor="divider"
            borderRadius={2}
            bgcolor="background.default"
          >
            <TextField
              fullWidth
              size="small"
              label="URL stream votazioni"
              value={publicVotesApiUrl}
              InputProps={{ readOnly: true }}
              onFocus={(e) => e.target.select()}
            />
            <Box
              mt={2}
              display="flex"
              gap={1}
              justifyContent="flex-end"
              flexWrap="wrap"
            >
              <Button
                variant="outlined"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(publicVotesApiUrl);
                    alert("Link copiato negli appunti");
                  } catch {
                    alert(
                      "Impossibile copiare automaticamente. Seleziona e copia il link.",
                    );
                  }
                }}
              >
                Copia link
              </Button>
              <Button
                variant="contained"
                onClick={() =>
                  window.open(
                    publicVotesApiUrl,
                    "_blank",
                    "noopener,noreferrer",
                  )
                }
              >
                Apri
              </Button>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box mt={4} mb={4}>
          <Typography variant="h6" gutterBottom>
            Gestione Eventi
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Crea, pubblica o nascondi eventi. Solo gli eventi pubblicati
            appaiono nella home.
          </Typography>

          <Box display="flex" justifyContent="flex-end" mb={2}>
            <Button
              variant="outlined"
              onClick={handleSeedEvents}
              disabled={seedingEvents}
            >
              {seedingEvents ? "Seeding..." : "Seed esempi"}
            </Button>
          </Box>

          <Box
            p={3}
            border="1px solid"
            borderColor="divider"
            borderRadius={2}
            bgcolor="background.default"
            sx={{ mb: 3 }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Key (es. sanremo)"
                  value={newEvent.key}
                  onChange={(e) =>
                    setNewEvent((prev) => ({ ...prev, key: e.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Titolo"
                  value={newEvent.title}
                  onChange={(e) =>
                    setNewEvent((prev) => ({
                      ...prev,
                      title: e.target.value,
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Sottotitolo"
                  value={newEvent.subtitle}
                  onChange={(e) =>
                    setNewEvent((prev) => ({
                      ...prev,
                      subtitle: e.target.value,
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={1} display="flex" alignItems="center">
                <Button
                  variant="contained"
                  onClick={handleCreateEvent}
                  disabled={
                    creating || !newEvent.key.trim() || !newEvent.title.trim()
                  }
                >
                  {creating ? "Creazione..." : "Crea"}
                </Button>
              </Grid>
            </Grid>
          </Box>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Eventi
            </Typography>
            {eventsError && (
              <Typography color="error" variant="body2" gutterBottom>
                {eventsError}
              </Typography>
            )}
            <List dense>
              {events.map((ev) => (
                <React.Fragment key={ev.key}>
                  <ListItem>
                    <ListItemText
                      primary={`${ev.title} (${ev.key})`}
                      secondary={ev.subtitle}
                    />
                    <Chip
                      label={ev.status === "published" ? "Pubblicato" : "Bozza"}
                      color={ev.status === "published" ? "success" : "default"}
                      size="small"
                      sx={{ mr: 2 }}
                    />
                    <Button
                      variant="outlined"
                      onClick={() => togglePublish(ev)}
                      sx={{ mr: 1 }}
                    >
                      {ev.status === "published" ? "Nascondi" : "Pubblica"}
                    </Button>
                    <Button
                      variant="text"
                      onClick={() => navigate(`/${ev.key}`)}
                    >
                      Vai
                    </Button>
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
              {events.length === 0 && (
                <ListItem>
                  <ListItemText primary="Nessun evento presente" />
                </ListItem>
              )}
            </List>
          </Paper>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box mt={4} mb={4}>
          <Typography variant="h6" gutterBottom>
            Gestione Fanta-Sanremo
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Inserisci la Top 5 ufficiale e svela i risultati.
          </Typography>

          <Box
            p={3}
            border="1px solid"
            borderColor="divider"
            borderRadius={2}
            bgcolor="background.default"
          >
            {[0, 1, 2, 3, 4].map((index) => (
              <FormControl fullWidth size="small" key={index} sx={{ mb: 2 }}>
                <InputLabel>{index + 1}° Classificato</InputLabel>
                <Select
                  value={fantaResults[index] || ""}
                  label={`${index + 1}° Classificato`}
                  onChange={(e) =>
                    handleFantaArtistChange(index, e.target.value)
                  }
                >
                  <MenuItem value="">
                    <em>Nessuno</em>
                  </MenuItem>
                  {songs.map((song) => (
                    <MenuItem key={song.key} value={song.key}>
                      {song.artist} - {song.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ))}

            <Box
              mt={3}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              gap={2}
              flexWrap="wrap"
            >
              <Button
                variant={fantaLocked ? "contained" : "outlined"}
                color={fantaLocked ? "error" : "primary"}
                onClick={toggleLock}
                disabled={savingFanta}
              >
                {fantaLocked ? "Sblocca Pronostici" : "Blocca Pronostici"}
              </Button>

              <Button
                variant={fantaRevealed ? "contained" : "outlined"}
                color={fantaRevealed ? "error" : "warning"}
                onClick={toggleReveal}
                disabled={savingFanta}
              >
                {fantaRevealed ? "Nascondi Risultati" : "Svela Risultati"}
              </Button>

              <Button
                variant="contained"
                color="primary"
                onClick={saveFantaResults}
                disabled={savingFanta}
                startIcon={<Save />}
              >
                {savingFanta ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Salva Risultati"
                )}
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default AdminPage;
